import React, { useState, useEffect, useCallback, useRef } from 'react';
import MermaidDiagram from '../MermaidDiagram';
import PromptBar from './PromptBar';
import StudioErrorBoundary from './ErrorBoundary';
import {
  Artifact, Project, ProjectSummary, ArtifactType, NewArtifact, studioApi,
  formatDate, typeLabel, typeLabelPlural, docTypeLabel,
  renderMarkdown, downloadSVG, downloadPNG,
} from './types';
import './Studio.css';

// ===================== Toast Notification =====================

interface ToastMsg { message: string; type: 'error' | 'success' }

const Toast: React.FC<{ toast: ToastMsg | null }> = ({ toast }) => {
  if (!toast) return null;
  return (
    <div className={`studio-toast studio-toast-${toast.type}`}>
      {toast.message}
    </div>
  );
};

// ===================== Smart Polling Hook =====================

function useSmartPolling(callback: () => void, intervalMs: number, enabled: boolean) {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (document.visibilityState === 'visible') savedCallback.current();
    };
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, enabled]);
}

// ===================== Debounce Hook =====================

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ===================== Process View =====================

const ProcessView: React.FC<{
  artifact: Artifact;
  projectId: string;
  onUpdated: () => void;
  onError: (msg: string) => void;
}> = ({ artifact, projectId, onUpdated, onError }) => {
  const { mermaid, beschreibung, hinweise } = artifact.data;
  const [tab, setTab] = useState<'diagram' | 'details' | 'editor'>('diagram');
  const [editCode, setEditCode] = useState(mermaid || '');
  const [saving, setSaving] = useState(false);
  const debouncedCode = useDebounce(editCode, 400);

  useEffect(() => { setEditCode(mermaid || ''); }, [mermaid]);

  const handleSaveMermaid = async () => {
    setSaving(true);
    try {
      await studioApi.updateArtifact(projectId, artifact.id, { data: { mermaid: editCode } });
      onUpdated();
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const handleExport = (format: 'svg' | 'png') => {
    const el = document.querySelector('.studio-diagram-container .mermaid-diagram-container svg') as SVGSVGElement;
    if (!el) { onError('Kein Diagramm zum Exportieren gefunden.'); return; }
    if (format === 'svg') downloadSVG(el, artifact.title);
    else downloadPNG(el, artifact.title);
  };

  return (
    <div className="studio-artifact-view">
      <div className="studio-view-tabs">
        <button className={`studio-tab ${tab === 'diagram' ? 'active' : ''}`} onClick={() => setTab('diagram')}>Diagramm</button>
        <button className={`studio-tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>Details</button>
        <button className={`studio-tab ${tab === 'editor' ? 'active' : ''}`} onClick={() => setTab('editor')}>Editor</button>
        {tab === 'diagram' && (
          <div className="studio-tab-actions">
            <button className="studio-btn-xs" onClick={() => handleExport('svg')}>SVG</button>
            <button className="studio-btn-xs" onClick={() => handleExport('png')}>PNG</button>
          </div>
        )}
      </div>

      {tab === 'diagram' && mermaid && (
        <div className="studio-diagram-container">
          <MermaidDiagram code={mermaid} />
        </div>
      )}

      {tab === 'editor' && (
        <div className="studio-editor-split">
          <div className="studio-editor-pane">
            <div className="studio-editor-header">
              <span>Mermaid Code</span>
              <button className="studio-btn-sm studio-btn-green" onClick={handleSaveMermaid} disabled={saving}>
                {saving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
            <textarea
              className="studio-code-editor"
              value={editCode}
              onChange={e => setEditCode(e.target.value)}
              spellCheck={false}
            />
          </div>
          <div className="studio-preview-pane">
            <div className="studio-editor-header"><span>Vorschau</span></div>
            <div className="studio-preview-content">
              <MermaidDiagram code={debouncedCode} />
            </div>
          </div>
        </div>
      )}

      {tab === 'details' && beschreibung && (
        <div className="studio-process-details">
          {beschreibung.problem && <Section title="Problem"><p>{beschreibung.problem}</p></Section>}
          {beschreibung.scope && <Section title="Scope"><p>{beschreibung.scope}</p></Section>}
          {beschreibung.rollen && beschreibung.rollen.length > 0 && (
            <Section title="Rollen">
              <div className="studio-roles-grid">
                {beschreibung.rollen.map((r, i) => (
                  <div key={i} className="studio-role-card">
                    <strong>{r.name}</strong><span>{r.verantwortung}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
          {beschreibung.prozesse && beschreibung.prozesse.length > 0 && (
            <Section title="Prozessschritte">
              {beschreibung.prozesse.map((p, i) => (
                <div key={i} className="studio-step-card">
                  <h5>{p.name}</h5>
                  {p.ziel && <p className="studio-step-goal">{p.ziel}</p>}
                  <div className="studio-step-lists">
                    {p.eingaben?.length ? <ListBlock label="Eingaben" items={p.eingaben} /> : null}
                    {p.ausgaben?.length ? <ListBlock label="Ausgaben" items={p.ausgaben} /> : null}
                    {p.regeln?.length ? <ListBlock label="Regeln" items={p.regeln} /> : null}
                    {p.risiken?.length ? <ListBlock label="Risiken" items={p.risiken} /> : null}
                  </div>
                </div>
              ))}
            </Section>
          )}
          {beschreibung.akzeptanzkriterien?.length ? (
            <Section title="Akzeptanzkriterien">
              <ul className="studio-criteria-list">{beschreibung.akzeptanzkriterien.map((k, i) => <li key={i}>{k}</li>)}</ul>
            </Section>
          ) : null}
          {hinweise?.length ? (
            <div className="studio-notes-section">
              <h4>Hinweise</h4>
              <ul>{hinweise.map((h, i) => <li key={i}>{h}</li>)}</ul>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ===================== Wireframe View =====================

const VIEWPORTS = [
  { value: 'desktop' as const, label: 'Desktop', width: '100%' },
  { value: 'tablet' as const, label: 'Tablet', width: '768px' },
  { value: 'mobile' as const, label: 'Mobile', width: '375px' },
];

const WireframeView: React.FC<{
  artifact: Artifact;
  projectId: string;
  onUpdated: () => void;
  onError: (msg: string) => void;
}> = ({ artifact, projectId, onUpdated, onError }) => {
  const { html, description, viewport } = artifact.data;
  const [editing, setEditing] = useState(false);
  const [editHtml, setEditHtml] = useState(html || '');
  const [saving, setSaving] = useState(false);
  const [activeViewport, setActiveViewport] = useState<'desktop' | 'tablet' | 'mobile'>(viewport || 'desktop');

  useEffect(() => { setEditHtml(html || ''); }, [html]);
  useEffect(() => { setActiveViewport(viewport || 'desktop'); }, [viewport]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await studioApi.updateArtifact(projectId, artifact.id, { data: { html: editHtml } });
      onUpdated();
      setEditing(false);
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const handleViewportChange = async (vp: 'desktop' | 'tablet' | 'mobile') => {
    setActiveViewport(vp);
    try {
      await studioApi.updateArtifact(projectId, artifact.id, { data: { viewport: vp } });
    } catch { /* viewport change is non-critical */ }
  };

  const vpWidth = VIEWPORTS.find(v => v.value === activeViewport)?.width || '100%';

  return (
    <div className="studio-artifact-view">
      <div className="studio-view-tabs">
        <button className={`studio-tab ${!editing ? 'active' : ''}`} onClick={() => setEditing(false)}>Vorschau</button>
        <button className={`studio-tab ${editing ? 'active' : ''}`} onClick={() => setEditing(true)}>HTML Editor</button>
        <div className="studio-tab-actions">
          {VIEWPORTS.map(vp => (
            <button
              key={vp.value}
              className={`studio-viewport-btn ${activeViewport === vp.value ? 'active' : ''}`}
              onClick={() => handleViewportChange(vp.value)}
              title={vp.label}
            >
              {vp.label}
            </button>
          ))}
        </div>
      </div>

      {description && <p className="studio-wireframe-desc">{description}</p>}

      {editing ? (
        <div className="studio-editor-split">
          <div className="studio-editor-pane">
            <div className="studio-editor-header">
              <span>HTML/CSS Code</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="studio-btn-sm" onClick={() => setEditing(false)}>Abbrechen</button>
                <button className="studio-btn-sm studio-btn-green" onClick={handleSave} disabled={saving}>
                  {saving ? 'Speichert...' : 'Speichern'}
                </button>
              </div>
            </div>
            <textarea
              className="studio-code-editor"
              value={editHtml}
              onChange={e => setEditHtml(e.target.value)}
              spellCheck={false}
            />
          </div>
          <div className="studio-preview-pane">
            <div className="studio-editor-header"><span>Live-Vorschau</span></div>
            <div className="studio-wireframe-preview" style={{ maxWidth: vpWidth }}>
              <iframe srcDoc={editHtml} title="preview" className="studio-wireframe-iframe" sandbox="allow-scripts" />
            </div>
          </div>
        </div>
      ) : (
        <div className="studio-wireframe-preview" style={{ maxWidth: vpWidth, margin: '0 auto' }}>
          <iframe srcDoc={html} title={artifact.title} className="studio-wireframe-iframe" sandbox="allow-scripts" />
        </div>
      )}
    </div>
  );
};

// ===================== Document View =====================

const DocumentView: React.FC<{
  artifact: Artifact;
  projectId: string;
  onUpdated: () => void;
  onError: (msg: string) => void;
}> = ({ artifact, projectId, onUpdated, onError }) => {
  const { content, documentType } = artifact.data;
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(content || '');
  const [saving, setSaving] = useState(false);
  const debouncedContent = useDebounce(editContent, 300);

  useEffect(() => { setEditContent(content || ''); }, [content]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await studioApi.updateArtifact(projectId, artifact.id, { data: { content: editContent } });
      onUpdated();
      setEditing(false);
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const handleExportMd = () => {
    const blob = new Blob([content || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title.replace(/[^a-z0-9]/gi, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="studio-artifact-view">
      <div className="studio-view-tabs">
        <button className={`studio-tab ${!editing ? 'active' : ''}`} onClick={() => setEditing(false)}>Vorschau</button>
        <button className={`studio-tab ${editing ? 'active' : ''}`} onClick={() => setEditing(true)}>Editor</button>
        <div className="studio-tab-actions">
          {documentType && <span className="studio-doc-badge">{docTypeLabel(documentType)}</span>}
          <button className="studio-btn-xs" onClick={handleExportMd}>Export .md</button>
        </div>
      </div>

      {editing ? (
        <div className="studio-editor-split">
          <div className="studio-editor-pane">
            <div className="studio-editor-header">
              <span>Markdown</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="studio-btn-sm" onClick={() => setEditing(false)}>Abbrechen</button>
                <button className="studio-btn-sm studio-btn-green" onClick={handleSave} disabled={saving}>
                  {saving ? 'Speichert...' : 'Speichern'}
                </button>
              </div>
            </div>
            <textarea
              className="studio-code-editor"
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              spellCheck={false}
            />
          </div>
          <div className="studio-preview-pane">
            <div className="studio-editor-header"><span>Vorschau</span></div>
            <div className="studio-markdown-rendered studio-preview-content"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(debouncedContent) }}
            />
          </div>
        </div>
      ) : (
        <div className="studio-markdown-rendered" dangerouslySetInnerHTML={{ __html: renderMarkdown(content || '') }} />
      )}
    </div>
  );
};

// ===================== Helpers =====================

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="studio-detail-section"><h4>{title}</h4>{children}</div>
);

const ListBlock: React.FC<{ label: string; items: string[] }> = ({ label, items }) => (
  <div><strong>{label}:</strong><ul>{items.map((it, i) => <li key={i}>{it}</li>)}</ul></div>
);

// ===================== Project Detail =====================

const ProjectDetail: React.FC<{
  projectId: string;
  onBack: () => void;
}> = ({ projectId, onBack }) => {
  const [project, setProject] = useState<Project | null>(null);
  const [selected, setSelected] = useState<Artifact | null>(null);
  const selectedRef = useRef<Artifact | null>(null);
  const [filter, setFilter] = useState<'all' | ArtifactType>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claudeAvailable, setClaudeAvailable] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Keep selectedRef in sync
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const showToast = useCallback((message: string, type: 'error' | 'success' = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const p = await studioApi.getProject(projectId);
      setProject(p);
      const sel = selectedRef.current;
      if (sel) {
        const updated = p.artifacts.find(a => a.id === sel.id);
        if (updated) setSelected(updated);
        else setSelected(null);
      }
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load(true);
    studioApi.health().then(h => setClaudeAvailable(h.claude));
  }, [load]);

  useSmartPolling(() => load(), 5000, true);

  // Keyboard: Escape to deselect
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingTitle) setEditingTitle(null);
        else setSelected(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingTitle]);

  const handleDeleteArtifact = async (id: string) => {
    if (!window.confirm('Artefakt wirklich loeschen?')) return;
    setActionLoading(id);
    try {
      await studioApi.deleteArtifact(projectId, id);
      if (selectedRef.current?.id === id) setSelected(null);
      showToast('Artefakt geloescht.', 'success');
      load();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Loeschen fehlgeschlagen');
    }
    setActionLoading(null);
  };

  const handleRenameArtifact = async (id: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    setActionLoading(id);
    try {
      await studioApi.updateArtifact(projectId, id, { title: newTitle.trim() });
      setEditingTitle(null);
      load();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Umbenennen fehlgeschlagen');
    }
    setActionLoading(null);
  };

  const handleDuplicate = async (artifact: Artifact) => {
    setActionLoading(artifact.id);
    try {
      const newArtifact: NewArtifact = {
        type: artifact.type,
        title: `${artifact.title} (Kopie)`,
        data: { ...artifact.data },
      };
      await studioApi.addArtifact(projectId, newArtifact);
      showToast('Artefakt dupliziert.', 'success');
      load();
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Duplizieren fehlgeschlagen');
    }
    setActionLoading(null);
  };

  const handleExportProject = async () => {
    if (!project) return;
    try {
      const data = await studioApi.exportProject(projectId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/[^a-z0-9]/gi, '_')}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Projekt exportiert.', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    }
  };

  const handleBack = () => {
    if (isGenerating) {
      if (!window.confirm('Claude generiert gerade. Wirklich zurueck?')) return;
    }
    onBack();
  };

  if (loading && !project) return <div className="studio-loading">Projekt wird geladen...</div>;
  if (error && !project) return <div className="studio-error"><p>{error}</p><button className="studio-btn" onClick={onBack}>Zurueck</button></div>;
  if (!project) return null;

  const filtered = filter === 'all' ? project.artifacts : project.artifacts.filter(a => a.type === filter);
  const counts = { process: 0, wireframe: 0, document: 0 };
  project.artifacts.forEach(a => { if (counts[a.type] !== undefined) counts[a.type]++; });

  return (
    <div className="studio-project-detail">
      <Toast toast={toast} />

      {/* Header */}
      <div className="studio-project-header">
        <button className="studio-btn-back" onClick={handleBack}>&larr; Projekte</button>
        <div className="studio-project-title-area">
          <h2>{project.name}</h2>
          {project.description && <p className="studio-project-desc">{project.description}</p>}
        </div>
        <div className="studio-project-stats">
          <span className="studio-stat type-process">{counts.process} Prozesse</span>
          <span className="studio-stat type-wireframe">{counts.wireframe} Wireframes</span>
          <span className="studio-stat type-document">{counts.document} Dokumente</span>
          <button className="studio-btn-xs" onClick={handleExportProject} title="Projekt exportieren">Export</button>
        </div>
      </div>

      {/* Prompt Bar */}
      <PromptBar
        projectId={projectId}
        onArtifactCreated={() => load()}
        claudeAvailable={claudeAvailable}
        onGeneratingChange={setIsGenerating}
      />

      {/* Content */}
      <div className="studio-project-content">
        {/* Sidebar */}
        <div className="studio-artifact-list">
          <div className="studio-filter-bar">
            {(['all', 'process', 'wireframe', 'document'] as const).map(f => (
              <button key={f} className={`studio-filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                {f === 'all' ? `Alle (${project.artifacts.length})` : `${typeLabelPlural(f)} (${counts[f]})`}
              </button>
            ))}
          </div>

          <div className="studio-artifact-cards">
            {filtered.length === 0 ? (
              <div className="studio-empty-list">
                <p>Keine Artefakte</p>
                <p className="studio-hint">Nutze den Prompt oben oder Claude Code CLI</p>
              </div>
            ) : (
              filtered.map(a => (
                <div key={a.id} className={`studio-artifact-card ${selected?.id === a.id ? 'selected' : ''} type-${a.type} ${actionLoading === a.id ? 'loading' : ''}`}>
                  <button className="studio-artifact-card-main" onClick={() => setSelected(a)}>
                    <span className={`studio-artifact-dot type-${a.type}`} />
                    <div className="studio-artifact-card-info">
                      {editingTitle === a.id ? (
                        <input
                          autoFocus
                          defaultValue={a.title}
                          className="studio-rename-input"
                          onBlur={e => handleRenameArtifact(a.id, e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameArtifact(a.id, (e.target as HTMLInputElement).value);
                            if (e.key === 'Escape') setEditingTitle(null);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <>
                          <span className="studio-artifact-card-type">{typeLabel(a.type)}</span>
                          <span className="studio-artifact-card-title">{a.title}</span>
                          <span className="studio-artifact-card-date">{formatDate(a.updatedAt)}</span>
                        </>
                      )}
                    </div>
                  </button>
                  <div className="studio-artifact-card-actions">
                    <button title="Umbenennen" onClick={e => { e.stopPropagation(); setEditingTitle(a.id); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button title="Duplizieren" onClick={e => { e.stopPropagation(); handleDuplicate(a); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    </button>
                    <button title="Loeschen" className="studio-delete-action" onClick={e => { e.stopPropagation(); handleDeleteArtifact(a.id); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Viewer */}
        <div className="studio-artifact-viewer">
          {selected ? (
            <>
              <div className="studio-viewer-header">
                <h3>{selected.title}</h3>
                <span className={`studio-type-badge type-${selected.type}`}>{typeLabel(selected.type)}</span>
              </div>
              <div className="studio-viewer-content">
                {selected.type === 'process' && <ProcessView artifact={selected} projectId={projectId} onUpdated={() => load()} onError={msg => showToast(msg)} />}
                {selected.type === 'wireframe' && <WireframeView artifact={selected} projectId={projectId} onUpdated={() => load()} onError={msg => showToast(msg)} />}
                {selected.type === 'document' && <DocumentView artifact={selected} projectId={projectId} onUpdated={() => load()} onError={msg => showToast(msg)} />}
              </div>
            </>
          ) : (
            <div className="studio-no-selection">
              <div className="studio-no-selection-content">
                <h3>Claude Studio</h3>
                <p>Waehle ein Artefakt oder erstelle ein neues mit dem Prompt oben.</p>
                <div className="studio-example-prompts">
                  <code>"Erstelle einen Prozess fuer die Kundenregistrierung"</code>
                  <code>"Erstelle ein Wireframe fuer die Login-Seite"</code>
                  <code>"Schreibe ein Anforderungsdokument fuer das Kundenportal"</code>
                </div>
                <p className="studio-hint">Escape = Auswahl aufheben</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== Project List =====================

const ProjectList: React.FC<{ onSelect: (id: string) => void }> = ({ onSelect }) => {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'error' | 'success' = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    try {
      const p = await studioApi.listProjects();
      setProjects(p);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  useSmartPolling(load, 5000, true);

  const handleCreate = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const p = await studioApi.createProject(newName.trim(), newDesc.trim());
      setNewName('');
      setNewDesc('');
      setShowCreate(false);
      onSelect(p.id);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Erstellen fehlgeschlagen');
    }
    setCreating(false);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm('Projekt wirklich loeschen?')) return;
    setDeleteLoading(id);
    try {
      await studioApi.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast('Projekt geloescht.', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Loeschen fehlgeschlagen');
    }
    setDeleteLoading(null);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        const imported = await studioApi.importProject(data);
        showToast('Projekt importiert.', 'success');
        onSelect(imported.id);
      } catch (err: unknown) {
        showToast(err instanceof Error ? err.message : 'Import fehlgeschlagen');
      }
    };
    input.click();
  };

  const filtered = search.trim()
    ? projects.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase()))
    : projects;

  if (loading && projects.length === 0) return <div className="studio-loading">Projekte werden geladen...</div>;

  return (
    <div className="studio-project-list">
      <Toast toast={toast} />

      {/* Header */}
      <div className="studio-list-header">
        <h2>Projekte</h2>
        <div className="studio-list-header-actions">
          <button className="studio-btn" onClick={handleImport}>Import</button>
          <button className="studio-btn studio-btn-primary" onClick={() => setShowCreate(true)}>+ Neues Projekt</button>
        </div>
      </div>

      {/* Search */}
      {projects.length > 0 && (
        <input
          className="studio-input studio-search-input"
          placeholder="Projekte suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      )}

      {/* Create Dialog */}
      {showCreate && (
        <div className="studio-create-dialog">
          <input
            autoFocus
            placeholder="Projektname"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            className="studio-input"
          />
          <input
            placeholder="Beschreibung (optional)"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            className="studio-input"
          />
          <div className="studio-create-actions">
            <button className="studio-btn" onClick={() => setShowCreate(false)}>Abbrechen</button>
            <button className="studio-btn studio-btn-primary" onClick={handleCreate} disabled={!newName.trim() || creating}>
              {creating ? 'Erstellt...' : 'Erstellen'}
            </button>
          </div>
        </div>
      )}

      {/* Project Grid */}
      {filtered.length === 0 && !showCreate ? (
        projects.length === 0 ? (
          <div className="studio-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
              <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
            </svg>
            <h2>Willkommen im Claude Studio</h2>
            <p>Erstelle dein erstes Projekt und lass Claude Prozesse, Wireframes und Dokumente generieren.</p>
            <button className="studio-btn studio-btn-primary studio-btn-lg" onClick={() => setShowCreate(true)}>
              Erstes Projekt erstellen
            </button>
          </div>
        ) : (
          <div className="studio-empty-list">
            <p>Keine Projekte gefunden fuer "{search}"</p>
          </div>
        )
      ) : (
        <div className="studio-projects-grid">
          {filtered.map(p => (
            <button key={p.id} className={`studio-project-card ${deleteLoading === p.id ? 'loading' : ''}`} onClick={() => onSelect(p.id)}>
              <div className="studio-project-card-header">
                <h3>{p.name}</h3>
                <button className="studio-card-delete" onClick={e => handleDelete(e, p.id)} title="Loeschen">&times;</button>
              </div>
              {p.description && <p className="studio-project-card-desc">{p.description}</p>}
              <div className="studio-project-card-stats">
                {p.artifacts.process > 0 && <span className="type-process">{p.artifacts.process} Prozesse</span>}
                {p.artifacts.wireframe > 0 && <span className="type-wireframe">{p.artifacts.wireframe} Wireframes</span>}
                {p.artifacts.document > 0 && <span className="type-document">{p.artifacts.document} Dokumente</span>}
                {p.totalArtifacts === 0 && <span className="studio-empty-badge">Leer</span>}
              </div>
              <div className="studio-project-card-date">Aktualisiert: {formatDate(p.updatedAt)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== Main Studio =====================

const Studio: React.FC = () => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  return (
    <StudioErrorBoundary>
      {selectedProjectId ? (
        <ProjectDetail projectId={selectedProjectId} onBack={() => setSelectedProjectId(null)} />
      ) : (
        <ProjectList onSelect={id => setSelectedProjectId(id)} />
      )}
    </StudioErrorBoundary>
  );
};

export default Studio;
